/**
 * 参考文献精确匹配 PM 确认卡片
 *
 * 流程（启动机翻前必过，独立确认步骤）：
 * 1. PM 进入项目详情页，参考信息一览表确认后调 preview 接口
 * 2. 卡片列出所有精确匹配的 (source, target, hitCount)
 * 3. PM 默认勾选所有匹配项（意为"采纳自动匹配"），可取消不想要的
 * 4. PM 点"确认采纳"，前端把"未勾选的 source 列表"作为 excludedSources 调 confirm 接口
 * 5. 后端保存 excludedSources + referenceMatchesConfirmed=true
 * 6. Engine 启动机翻时拉取 exclusions 注入 ReferenceMatcher，跳过这些 source
 *
 * 注意：excludedSources 存的是"PM 取消采纳的 source"，即未勾选项
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Check, X, Loader2, AlertCircle, FileText } from 'lucide-react'
import { toast } from 'sonner'
import { useState, useMemo, useEffect } from 'react'

interface ReferenceMatchConfirmCardProps {
  projectId: string
  /** 参考信息一览表是否已确认（未确认不展示本卡片，避免提前看到匹配） */
  referenceConfirmed: boolean
  /** 本卡片是否已确认过（已确认则折叠展示） */
  matchesConfirmed: boolean
}

interface MatchItem {
  source: string
  target: string
  hitCount: number
}

export function ReferenceMatchConfirmCard({
  projectId,
  referenceConfirmed,
  matchesConfirmed,
}: ReferenceMatchConfirmCardProps) {
  const queryClient = useQueryClient()

  // 仅在参考信息一览表已确认时拉取匹配预览
  const { data, isLoading, error } = useQuery({
    queryKey: ['reference-matches-preview', projectId],
    queryFn: () => projectsApi.previewReferenceMatches(projectId),
    enabled: referenceConfirmed && !matchesConfirmed,
  })

  const matches: MatchItem[] = useMemo(() => (data as any)?.matches || [], [data])
  const serverExcluded: string[] = useMemo(() => (data as any)?.excludedSources || [], [data])

  // 本地维护"被取消采纳的 source 集合"
  // 默认空（即所有匹配项默认采纳），PM 主动点 × 把某项加入排除集合
  const [excludedSet, setExcludedSet] = useState<Set<string>>(new Set())

  // 服务端已有排除列表时同步到本地（首次加载或重新拉取时）
  useEffect(() => {
    if (serverExcluded.length > 0) {
      setExcludedSet(new Set(serverExcluded))
    }
  }, [serverExcluded])

  const confirmMutation = useMutation({
    mutationFn: (excludedSources: string[]) =>
      projectsApi.confirmReferenceMatches(projectId, excludedSources),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast.success('参考文献匹配已确认，可以启动机翻')
    },
    onError: (err: any) => {
      toast.error(err.message || '确认失败')
    },
  })

  // loading 中
  if (referenceConfirmed && !matchesConfirmed && isLoading) {
    return (
      <Card className="border-info/30 bg-info/5">
        <CardContent className="py-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-info" />
          <p className="text-sm text-muted-foreground mt-2">正在预览参考文献精确匹配...</p>
        </CardContent>
      </Card>
    )
  }

  // 拉取失败
  if (referenceConfirmed && !matchesConfirmed && error) {
    return (
      <Card className="border-destructive/50 bg-destructive/5">
        <CardContent className="py-8 text-center">
          <AlertCircle className="h-6 w-6 mx-auto text-destructive" />
          <p className="text-sm text-muted-foreground mt-2">加载参考文献匹配预览失败</p>
        </CardContent>
      </Card>
    )
  }

  // 后端自动确认（无双语参考文件）—— 不展示卡片
  if ((data as any)?.autoConfirmed) {
    return null
  }

  // 已确认 + 无任何匹配 —— 不展示卡片
  if (matchesConfirmed && matches.length === 0) {
    return null
  }

  // 未到展示时机：参考信息一览表未确认
  if (!referenceConfirmed) {
    return null
  }

  // 已确认匹配后，折叠展示一个轻提示（让 PM 知道匹配已确认过）
  if (matchesConfirmed) {
    return (
      <Card className="border-success/30 bg-success/5">
        <CardContent className="py-3 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-success" />
            <span>参考文献精确匹配已确认</span>
            <Badge variant="outline" className="text-[10px] h-5 bg-success/10 text-success-foreground border-success/30">
              {matches.length} 项匹配
            </Badge>
            {excludedSet.size > 0 && (
              <Badge variant="outline" className="text-[10px] h-5 bg-muted/40 text-muted-foreground">
                已排除 {excludedSet.size} 项
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  // 主视图：列出匹配项供 PM 取消采纳
  const toggleExclude = (source: string) => {
    const next = new Set(excludedSet)
    if (next.has(source)) {
      next.delete(source)
    } else {
      next.add(source)
    }
    setExcludedSet(next)
  }

  const handleConfirm = () => {
    confirmMutation.mutate(Array.from(excludedSet))
  }

  const handleConfirmAll = () => {
    // 全部采纳（清空排除集合）
    setExcludedSet(new Set())
    confirmMutation.mutate([])
  }

  if (matches.length === 0) {
    // 没有任何精确匹配：直接给个空状态卡片，PM 确认后放行
    return (
      <Card className="border-info/30 bg-info/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            参考文献精确匹配预览
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            当前稿件没有与参考文献完全一致的段落，无需排除任何匹配项。
          </p>
          <div className="flex justify-end">
            <Button size="sm" onClick={handleConfirm} disabled={confirmMutation.isPending}>
              {confirmMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              确认并继续
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-info/30 bg-info/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              参考文献精确匹配预览
            </CardTitle>
            <Badge variant="outline" className="bg-info/10 text-info-foreground border-info/30">
              {matches.length} 项匹配
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleConfirmAll}
              disabled={confirmMutation.isPending}
            >
              全部采纳
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={confirmMutation.isPending}
            >
              {confirmMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-1" />
              )}
              确认采纳 ({matches.length - excludedSet.size}/{matches.length})
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          以下段落与参考文献完全一致，机翻时将自动采用参考译文。点 × 可取消采纳该项，让其走正常 LLM 翻译。
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {matches.map((m) => {
          const excluded = excludedSet.has(m.source)
          return (
            <div
              key={m.source}
              className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                excluded
                  ? 'border-muted bg-muted/20 opacity-60'
                  : 'border-info/30 bg-white hover:border-info/40'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium text-sm">{m.source}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-medium text-sm text-success-foreground">{m.target}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>命中 {m.hitCount} 段</span>
                  {excluded && (
                    <Badge variant="outline" className="text-[10px] h-5 bg-destructive/10 text-destructive border-destructive/30">
                      已取消采纳
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className={`h-7 w-7 p-0 ${
                  excluded
                    ? 'text-success hover:text-success-foreground hover:bg-success/20'
                    : 'text-destructive hover:text-destructive hover:bg-destructive/20'
                }`}
                onClick={() => toggleExclude(m.source)}
                title={excluded ? '恢复采纳' : '取消采纳'}
              >
                {excluded ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
              </Button>
            </div>
          )
        })}
        <p className="text-xs text-muted-foreground mt-3 text-center">
          确认后即可启动机翻
        </p>
      </CardContent>
    </Card>
  )
}
